/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package i18n

import settings.SettingsManager

/**
 * Manages locale persistence and initialization.
 * Integrates with SettingsManager for cross-platform storage.
 */
object LocaleManager {
    private var settingsManager: SettingsManager? = null
    private var initialized = false

    /**
     * Initialize the LocaleManager with a SettingsManager instance.
     * This should be called early in app startup.
     */
    fun initialize(settings: SettingsManager) {
        settingsManager = settings
        if (!initialized) {
            // Load saved language preference
            val savedLanguage = settings.getLanguage()
            val locale = AppLocale.fromCode(savedLanguage)
            Strings.setLocale(locale)
            initialized = true
        }
    }

    /**
     * Get the current locale.
     */
    fun getCurrentLocale(): AppLocale = Strings.getLocale()

    /**
     * Set the locale and persist the preference.
     */
    fun setLocale(locale: AppLocale) {
        Strings.setLocale(locale)
        settingsManager?.setLanguage(locale.code)
    }

    /**
     * Toggle between available locales.
     * Returns the new locale after toggling.
     */
    fun toggleLocale(): AppLocale {
        val current = getCurrentLocale()
        val available = AppLocale.entries
        val currentIndex = available.indexOf(current)
        val nextIndex = (currentIndex + 1) % available.size
        val newLocale = available[nextIndex]
        setLocale(newLocale)
        return newLocale
    }

    /**
     * Get all available locales.
     */
    fun getAvailableLocales(): List<AppLocale> = AppLocale.entries

    /**
     * Check if the manager has been initialized.
     */
    fun isInitialized(): Boolean = initialized
}
